import { useState, useMemo } from "react";
import { Card, Typography, Button, Row, Col, Progress, Badge, Select, Pagination } from "antd";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { faArrowLeft, faCalendar, faFileLines, faChartSimple, faFilePowerpoint, faFilter, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;

export function ProjectDetail({ project, onBack, onFolderClick, hidenMenu, ocultarData }) {
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);
    const allPeriods = useMemo(() => {
        return Object.keys(project.folders || {}).sort((a, b) => {
            return a.localeCompare(b);
        });
    }, [project.folders]);
    const filteredFolderData = useMemo(() => {
        const folders = project.folders || {};
        let folderEntries = Object.entries(folders);
        if (selectedPeriod) {
            folderEntries = folderEntries.filter(([folderName]) =>
                folderName === selectedPeriod
            );
        }
        return folderEntries.map(([folderName, stats]) => ({
            name: folderName,
            validados: stats.validated,
            sinValidar: stats.notValidated,
            sinRegistrar: stats.notRegistered,
            total: stats.total,
            validationRate: stats.total > 0 ? Math.round((stats.validated / stats.total) * 100) : 0,
        }));
    }, [project.folders, selectedPeriod]);
    const paginatedFolderData = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredFolderData.slice(startIndex, startIndex + pageSize);
    }, [filteredFolderData, currentPage, pageSize]);

    const totalPages = Math.ceil(filteredFolderData.length / pageSize);

    const handleFolderClick = (folderName) => {
        onFolderClick?.(folderName);
    };

    const handlePeriodChange = (value) => {
        setSelectedPeriod(value);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setSelectedPeriod('');
        setCurrentPage(1);
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
            setCurrentPage(1);
        }
    };

    const projectValidationRate =
        project.total > 0 ? Math.round((project.validated / project.total) * 100) : 0;

    return (
        <div style={{ padding: 16, marginBottom: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <Button type="ghost" onClick={onBack} style={{ color: "#0869C6" }}>
                    <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} />
                    Volver a Proyectos
                </Button>
            </div>
            <Card
                style={{
                    padding: 10,
                    borderRadius: 5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    backgroundColor: "#042956",
                    marginBottom: 20,
                }}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={2} style={{ margin: 0, color: "#ffffff" }}>
                            <FontAwesomeIcon onClick={hidenMenu} icon={ocultarData ? faEyeSlash : faEye} style={{ marginRight: 10 }} />
                            <FontAwesomeIcon icon={faFilePowerpoint} style={{ marginRight: 10 }} />
                            {project.projectName}
                        </Title>
                        <Paragraph type="secondary" style={{ margin: 0, color: "#ffffff" }}>
                            Detalles del proyecto y navegación por carpetas
                        </Paragraph>
                    </Col>
                    <Col>
                        <Badge
                            count={`${allPeriods.length} Períodos`}
                            style={{ backgroundColor: "#1677FF", color: "#fff", marginRight: 8 }}
                        />
                        <Badge
                            count={`${projectValidationRate}% Validado`}
                            style={{
                                backgroundColor:
                                    projectValidationRate >= 80
                                        ? "#009728"
                                        : projectValidationRate >= 50
                                            ? "#F8C340"
                                            : "#FF4D4F",
                                color: "#fff",
                            }}
                        />
                    </Col>
                </Row>
            </Card>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} md={6}>
                    <Card
                        hoverable
                        style={{
                            height: "125px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "16px",
                            borderRadius: "8px",
                        }}
                    >
                        <Row align="middle" justify="center" style={{ height: "100%" }}>
                            <Col style={{ textAlign: "center" }}>
                                <Text strong>Total Archivos</Text>
                                <div style={{ height: 8, margin: "0 auto" }}>
                                    <div
                                        style={{
                                            width: 20,
                                            height: 4,
                                            backgroundColor: "#338BC7",
                                            borderRadius: 2,
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#338BC7",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faFileLines} style={{ color: "#0869C6" }} />
                                    <Title style={{ paddingTop: 10 }} level={2}>{project.total}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card
                        hoverable
                        style={{
                            height: "125px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "16px",
                            borderRadius: "8px",
                        }}
                    >
                        <Row align="middle" justify="center" style={{ height: "100%" }}>
                            <Col style={{ textAlign: "center" }}>
                                <Text strong>Validados</Text>
                                <div style={{ height: 8, margin: "0 auto" }}>
                                    <div
                                        style={{
                                            width: 20,
                                            height: 4,
                                            backgroundColor: "#009728",
                                            borderRadius: 2,
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#338BC7",
                                    }}
                                >
                                    <CheckCircleOutlined style={{ color: "#009728", fontSize: 32 }} />
                                    <Title style={{ paddingTop: 10 }} level={2}>{project.validated}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card
                        hoverable
                        style={{
                            height: "125px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "16px",
                            borderRadius: "8px",
                        }}
                    >
                        <Row align="middle" justify="center" style={{ height: "100%" }}>
                            <Col style={{ textAlign: "center" }}>
                                <Text strong>Sin Validar</Text>
                                <div style={{ height: 8, margin: "0 auto" }}>
                                    <div
                                        style={{
                                            width: 20,
                                            height: 4,
                                            backgroundColor: "#F8C340",
                                            borderRadius: 2,
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#338BC7",
                                    }}
                                >
                                    <ExclamationCircleOutlined style={{ color: "#F8C340", fontSize: 32 }} />
                                    <Title style={{ paddingTop: 10 }} level={2}>{project.notValidated}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
                <Col xs={24} md={6}>
                    <Card
                        hoverable
                        style={{
                            height: "125px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "16px",
                            borderRadius: "8px",
                        }}
                    >
                        <Row align="middle" justify="center" style={{ height: "100%" }}>
                            <Col style={{ textAlign: "center" }}>
                                <Text strong>Sin Registrar</Text>
                                <div style={{ height: 8, margin: "0 auto" }}>
                                    <div
                                        style={{
                                            width: 20,
                                            height: 4,
                                            backgroundColor: "#FF4D4F",
                                            borderRadius: 2,
                                            margin: "0 auto",
                                        }}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                        fontSize: 28,
                                        fontWeight: "bold",
                                        color: "#338BC7",
                                    }}
                                >
                                    <CloseCircleOutlined style={{ color: "#FF4D4F", fontSize: 32 }} />
                                    <Title style={{ paddingTop: 10 }} level={2}>{project.notRegistered}</Title>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
            <Card
                style={{
                    borderRadius: 5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    overflow: "hidden",
                    marginBottom: 24,
                }}
            >
                <div
                    style={{
                        backgroundColor: "#042956",
                        color: "#fff",
                        fontSize: 16,
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 16px",
                        borderTopLeftRadius: 5,
                        borderTopRightRadius: 5,
                        marginBottom: 16,
                    }}
                >
                    <FontAwesomeIcon icon={faChartSimple} style={{ marginRight: 8 }} />
                    Archivos por Período {selectedPeriod && `- ${selectedPeriod}`}
                </div>

                <div
                    style={{
                        height: 320,
                        minHeight: 320,
                        padding: "0 10px 10px 0",
                    }}
                >
                    {filteredFolderData.length === 0 ? (
                        <div
                            style={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                            }}
                        >
                            <Text strong>No hay datos para mostrar la gráfica</Text>
                            <Text type="secondary">
                                {selectedPeriod
                                    ? `No se encontraron datos para el período: ${selectedPeriod}`
                                    : `Datos recibidos: ${filteredFolderData.length} registros`
                                }
                            </Text>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={filteredFolderData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 100 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
                                <XAxis
                                    dataKey="name"
                                    stroke="#333"
                                    fontSize={12}
                                    angle={-45}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis stroke="#333" fontSize={12} />
                                <Tooltip
                                    formatter={(value, name) => [
                                        <span style={{ color: "#333", fontWeight: "bold" }}>{value}</span>,
                                        name,
                                    ]}
                                    labelFormatter={(label) => `Período: ${label}`}
                                    contentStyle={{
                                        borderRadius: "6px",
                                        border: "1px solid #ddd",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    }}
                                />
                                <Legend
                                    verticalAlign="bottom"
                                    align="center"
                                    height={1}
                                    wrapperStyle={{
                                        bottom: 30,
                                    }}
                                    iconType="circle"
                                    iconSize={10}
                                />
                                <Bar
                                    dataKey="validados"
                                    name="Validados"
                                    fill="#009728"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={(data) => handleFolderClick(data.name)}
                                />
                                <Bar
                                    dataKey="sinValidar"
                                    name="Sin Validar"
                                    fill="#F8C340"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={(data) => handleFolderClick(data.name)}
                                />
                                <Bar
                                    dataKey="sinRegistrar"
                                    name="Sin Registrar"
                                    fill="#FF4D4F"
                                    cursor="pointer"
                                    stackId="a"
                                    radius={[0, 3, 3, 0]}
                                    onClick={(data) => handleFolderClick(data.name)}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Card>
            <Card
                style={{
                    padding: 10,
                    borderRadius: 5,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    backgroundColor: "#042956",
                    marginBottom: 20,
                }}
            >
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={5} style={{ margin: 0, color: "#ffffff" }}>
                            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 10 }} />
                            Períodos ({filteredFolderData.length})
                        </Title>
                    </Col>
                    <Col>
                        <Select
                            showSearch
                            style={{ width: 300 }}
                            placeholder="Filtrar por período"
                            value={selectedPeriod}
                            onChange={handlePeriodChange}
                            allowClear
                            onClear={handleClearFilter}
                            suffixIcon={<FontAwesomeIcon icon={faFilter} />}
                        >
                            <Option value="">Todos los períodos</Option>
                            {allPeriods.map(period => (
                                <Option key={period} value={period}>
                                    {period}
                                </Option>
                            ))}
                        </Select>
                        {selectedPeriod && (
                            <Button
                                type="link"
                                onClick={handleClearFilter}
                                style={{ color: '#fff', marginLeft: 10 }}
                            >
                                Limpiar filtro
                            </Button>
                        )}
                    </Col>
                </Row>
            </Card>
            {paginatedFolderData.length > 0 ? (
                <>
                    <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                        {paginatedFolderData.map((folder) => (
                            <Col xs={24} md={12} lg={8} key={folder.name}>
                                <Card
                                    hoverable
                                    onClick={() => handleFolderClick(folder.name)}
                                    style={{
                                        borderColor: "#338BC7",
                                        borderRadius: 12,
                                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                                        overflow: "hidden",
                                        display: 'flex',
                                        flexDirection: 'column',
                                        width: '100%',
                                        height: '100%',
                                    }}
                                >
                                    <div
                                        style={{
                                            backgroundColor: "#042956",
                                            color: "#fff",
                                            fontWeight: "bold",
                                            fontSize: 16,
                                            padding: "16px",
                                            borderTopLeftRadius: 12,
                                            borderTopRightRadius: 12,
                                            minHeight: 60,
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 8 }} />
                                        <Text
                                            ellipsis={{ tooltip: folder.name }}
                                            style={{
                                                color: '#fff',
                                                margin: 0,
                                                lineHeight: '1.3',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%'
                                            }}
                                        >
                                            {folder.name}
                                        </Text>
                                    </div>
                                    <div style={{
                                        padding: "16px",
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div>
                                            <div style={{ marginBottom: 12 }}>
                                                <Text type="secondary">Total:</Text>{" "}
                                                <Text strong style={{ fontSize: 15 }}>{folder.total}</Text>
                                            </div>
                                            <div style={{ marginBottom: 16 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                    <Text type="secondary">Validación:</Text>
                                                    <Text strong>{folder.validationRate}%</Text>
                                                </div>
                                                <Progress
                                                    percent={folder.validationRate}
                                                    showInfo={false}
                                                    strokeColor="#009728"
                                                    size="small"
                                                />
                                            </div>
                                        </div>
                                        <div style={{ marginTop: 'auto' }}>
                                            <Row justify="space-between" gutter={[8, 8]}>
                                                <Col style={{ textAlign: "center", flex: 1 }}>
                                                    <div style={{ padding: '4px' }}>
                                                        <Text strong style={{ color: "#009728", fontSize: 16 }}>
                                                            {folder.validados}
                                                        </Text>
                                                        <div style={{ fontSize: 12, color: '#666' }}>Valid.</div>
                                                    </div>
                                                </Col>
                                                <Col style={{ textAlign: "center", flex: 1 }}>
                                                    <div style={{ padding: '4px' }}>
                                                        <Text strong style={{ color: "#F8C340", fontSize: 16 }}>
                                                            {folder.sinValidar}
                                                        </Text>
                                                        <div style={{ fontSize: 12, color: '#666' }}>Pend.</div>
                                                    </div>
                                                </Col>
                                                <Col style={{ textAlign: "center", flex: 1 }}>
                                                    <div style={{ padding: '4px' }}>
                                                        <Text strong style={{ color: "#0869C6", fontSize: 16 }}>
                                                            {folder.sinRegistrar}
                                                        </Text>
                                                        <div style={{ fontSize: 12, color: '#666' }}>N/R</div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                    {totalPages > 1 && (
                        <div style={{
                            marginTop: 24,
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '20px 0',
                            borderTop: '1px solid #f0f0f0'
                        }}>
                            <Pagination
                                current={currentPage}
                                total={filteredFolderData.length}
                                pageSize={pageSize}
                                onChange={handlePageChange}
                                onShowSizeChange={handlePageChange}
                                showSizeChanger
                                pageSizeOptions={['6', '9', '12', '18']}
                                showTotal={(total, range) =>
                                    `Mostrando ${range[0]}-${range[1]} de ${total} períodos`
                                }
                                style={{ textAlign: 'center' }}
                            />
                        </div>
                    )}
                </>
            ) : (
                <Card style={{ textAlign: 'center', padding: 40 }}>
                    <Text type="secondary">
                        {selectedPeriod
                            ? `No se encontraron períodos para: "${selectedPeriod}"`
                            : "No hay períodos para mostrar"
                        }
                    </Text>
                </Card>
            )}
        </div>
    );
}